import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardStats } from "@/lib/api";
import {
  Send,
  Users,
  Inbox,
  MailCheck,
  MailX,
  Eye,
  MousePointerClick,
  ArrowRight,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const StatusBadge = ({ status }) => {
  const map = {
    draft: "badge-neutral",
    sending: "badge-warning",
    sent: "badge-success",
    failed: "badge-error",
  };
  return <span className={`badge ${map[status] || "badge-neutral"}`}>{status}</span>;
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardStats()
      .then((d) => setData(d))
      .finally(() => setLoading(false));
  }, []);

  const t = data?.totals || {};
  const trend = data?.trend || [];
  const recent = data?.recent_campaigns || [];

  return (
    <div className="p-8 max-w-[1400px]" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2">Overview</div>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "Outfit" }}
          >
            Delivery Command Center
          </h1>
          <p className="text-sm text-[#475569] mt-2 max-w-xl">
            Track every send, every open, every click. Built for inbox placement, not spam folders.
          </p>
        </div>
        <Link
          to="/campaigns/new"
          data-testid="btn-new-campaign-header"
          className="btn-primary inline-flex items-center gap-2"
        >
          <Send size={15} strokeWidth={2.2} />
          New Campaign
        </Link>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <ScoreCard
          testid="stat-emails-sent"
          label="Delivered"
          value={t.delivered ?? 0}
          hint={`${t.sent ?? 0} accepted by Resend`}
          Icon={MailCheck}
        />
        <ScoreCard
          testid="stat-delivery-rate"
          label="Delivery Rate"
          value={`${t.delivery_rate ?? 0}%`}
          hint={`${t.bounced ?? 0} bounced · ${t.failed ?? 0} errors`}
          Icon={Inbox}
          accent
        />
        <ScoreCard
          testid="stat-open-rate"
          label="Unique Open Rate"
          value={`${t.open_rate ?? 0}%`}
          hint={`${t.opens ?? 0} opens`}
          Icon={Eye}
        />
        <ScoreCard
          testid="stat-click-rate"
          label="Click Rate"
          value={`${t.click_rate ?? 0}%`}
          hint={`${t.clicks ?? 0} clicks`}
          Icon={MousePointerClick}
        />
      </div>

      {/* Chart + secondary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div
          data-testid="chart-engagement"
          className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="section-label mb-1">Engagement Trend</div>
              <div className="text-lg font-semibold tracking-tight text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
                Last 14 days
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#475569]">
              <LegendItem color="#002FA7" label="Sent" />
              <LegendItem color="#10B981" label="Opens" />
              <LegendItem color="#F59E0B" label="Clicks" />
            </div>
          </div>
          <div className="h-[280px]">
            {loading ? (
              <SkeletonLine />
            ) : trend.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="sent"
                    stroke="#002FA7"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#002FA7" }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="open"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#10B981" }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="click"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#F59E0B" }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div data-testid="workspace-summary" className="bg-white border border-[#E2E8F0] rounded-lg p-6">
          <div className="section-label mb-4">Workspace</div>
          <SummaryRow icon={Send} label="Total Campaigns" value={t.campaigns ?? 0} />
          <SummaryRow icon={Users} label="Audience Lists" value={t.lists ?? 0} />
          <SummaryRow icon={MailCheck} label="Total Contacts" value={t.contacts ?? 0} />
          <SummaryRow icon={MailX} label="Failed Sends" value={t.failed ?? 0} />

          <Link
            to="/lists"
            className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#002FA7] font-medium hover:underline"
            data-testid="link-manage-audiences"
          >
            Manage Audiences <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Recent campaigns */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="recent-campaigns">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div>
            <div className="section-label">Latest activity</div>
            <div className="text-lg font-semibold tracking-tight text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
              Recent Campaigns
            </div>
          </div>
          <Link
            to="/campaigns"
            className="text-sm text-[#002FA7] font-medium hover:underline"
            data-testid="link-view-all-campaigns"
          >
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#94A3B8]" data-testid="empty-campaigns">
            No campaigns yet. Create your first campaign to see it here.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <Th>Campaign</Th>
                <Th>Status</Th>
                <Th className="text-right">Sent</Th>
                <Th className="text-right">Opens</Th>
                <Th className="text-right">Clicks</Th>
                <Th className="text-right">Created</Th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c) => (
                <tr key={c.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm text-[#0F172A] font-medium">
                    <Link to={`/campaigns/${c.id}/report`} className="hover:text-[#002FA7]">
                      {c.name}
                    </Link>
                    <div className="text-xs text-[#94A3B8] font-normal mt-0.5 truncate max-w-md">
                      {c.subject}
                    </div>
                  </td>
                  <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                  <td className="px-6 py-4 text-sm text-right tabular-nums">{c.sent_count || 0}</td>
                  <td className="px-6 py-4 text-sm text-right tabular-nums">{c.unique_open_count || 0}</td>
                  <td className="px-6 py-4 text-sm text-right tabular-nums">{c.unique_click_count || 0}</td>
                  <td className="px-6 py-4 text-xs text-right text-[#94A3B8]">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const ScoreCard = ({ label, value, hint, Icon, accent, testid }) => (
  <div className="scorecard" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="section-label">{label}</div>
      <div
        className={`w-8 h-8 rounded-md flex items-center justify-center ${
          accent ? "bg-[#E5E9F6] text-[#002FA7]" : "bg-[#F1F5F9] text-[#475569]"
        }`}
      >
        <Icon size={15} strokeWidth={2.2} />
      </div>
    </div>
    <div
      className="text-3xl font-semibold tracking-tight text-[#0F172A] tabular-nums"
      style={{ fontFamily: "Outfit" }}
    >
      {value}
    </div>
    <div className="text-xs text-[#94A3B8]">{hint}</div>
  </div>
);

const LegendItem = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
    {label}
  </div>
);

const SummaryRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-[#F1F5F9] last:border-0">
    <div className="flex items-center gap-2.5">
      <Icon size={14} className="text-[#94A3B8]" strokeWidth={2} />
      <span className="text-sm text-[#475569]">{label}</span>
    </div>
    <span className="text-sm font-semibold text-[#0F172A] tabular-nums">{value}</span>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider text-[#64748B] text-left ${className}`}>
    {children}
  </th>
);

const SkeletonLine = () => (
  <div className="h-full w-full animate-pulse bg-gradient-to-r from-slate-50 to-slate-100 rounded" />
);

const EmptyChart = () => (
  <div className="h-full w-full flex items-center justify-center text-sm text-[#94A3B8]">
    Send your first campaign to unlock engagement analytics.
  </div>
);
